import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import React from "react";

import jobylonLogo from "../../assets/images/jobylon-logo.png";

interface JobylonBadgeProps {
	/** Overrides the default tooltip where the context needs a different wording. */
	title?: string;
	/** Rendered height in pixels; the width follows the logo's aspect ratio. */
	height?: number;
	/** Where the mark leads. Without it the logo is a plain marker. */
	href?: string;
	/** Dimmed, for an application the customer is already finished with. */
	muted?: boolean;
}

/**
 * Marks something as coming from the customer's Jobylon account.
 *
 * The vendor's own wordmark rather than a generic icon, because a recruiter
 * scanning a wall of roles recognises the brand faster than any label — and it
 * is the same asset the integrations directory shows, so the two agree.
 *
 * Given an `href` it becomes the way through to Jobylon, which for a role whose
 * candidates live there is the whole point of marking it.
 */
export const JobylonBadge = ({
	title = "Imported from Jobylon",
	height = 16,
	href,
	muted = false,
}: JobylonBadgeProps) => {
	const logo = (
		<Box
			component="img"
			src={jobylonLogo}
			alt="Jobylon"
			data-component="jobylon-badge"
			sx={{
				height,
				width: "auto",
				display: "block",
				verticalAlign: "middle",
				// Still the same mark, plainly past tense: a rejected or hired
				// application is worth knowing about and not worth acting on.
				...(muted ? { opacity: 0.45, filter: "grayscale(1)" } : {}),
			}}
		/>
	);

	return (
		<Tooltip title={title} data-component="jobylon-badge-tooltip">
			{href ? (
				<Box
					component="a"
					href={href}
					target="_blank"
					rel="noopener"
					data-component="jobylon-badge-link"
					// The card behind this opens the role when clicked, so the
					// link must keep the click to itself.
					onClick={(event: React.MouseEvent) => event.stopPropagation()}
					sx={{ display: "inline-flex", lineHeight: 0 }}
				>
					{logo}
				</Box>
			) : (
				logo
			)}
		</Tooltip>
	);
};

export default JobylonBadge;
